/**
 * CSV cell helpers
 * Formula guard and RFC 4180 quoting for one CSV cell.
 *
 * This is a copy of the backend rule in functions/src/utils/csvHelpers.ts
 * (the two packages share no code). Change both together.
 * Both are tested against test-vectors/csv-cell.json; new vectors go there.
 */

/**
 * Sanitize field to prevent CSV formula injection.
 * CSV files opened in spreadsheet applications can execute formulas
 * if a cell starts with =, +, -, @, tab (0x09), carriage return (0x0D)
 * or line feed (0x0A), or with a full-width ＝ ＋ － ＠ (U+FF1D, U+FF0B,
 * U+FF0D, U+FF20), which some locales read as formula characters. This
 * prefixes such values with a single quote to prevent formula execution.
 *
 * The character list is OWASP's (CSV Injection,
 * https://owasp.org/www-community/attacks/CSV_Injection, fetched 2026-10-08).
 *
 * Only the first character is guarded. Separator smuggling is NOT
 * prevented: a reader that splits on `;` or tab and honours `"` only at the
 * start of a field re-splits a quoted value mid-field, outside the first
 * column, so a quoted `x;=1+1` gives a cell `=1+1`. #2194 modelled this with
 * Python's csv module; Excel and LibreOffice were not tested. Inserting `'`
 * after an embedded separator was considered and rejected in #2192 (recorded
 * in #2194): it mangles legitimate data mid-value and still leaves a bypass
 * (`x; =1`). The alternatives (an XLSX export, a per-locale delimiter) stay
 * open in #2194.
 *
 * Sanitize first, then quote with `formatCsvField`; `csvCell` does both.
 *
 * @param value - Raw field value to sanitize
 * @returns Sanitized value safe for CSV export
 */
export const sanitizeCsvField = (value: string): string => {
  if (!value) return value;

  const firstChar = value.charAt(0);
  const isDangerous =
    firstChar === '=' ||
    firstChar === '+' ||
    firstChar === '-' ||
    firstChar === '@' ||
    firstChar === '\t' ||
    firstChar === '\r' ||
    firstChar === '\n' ||
    firstChar === '＝' ||
    firstChar === '＋' ||
    firstChar === '－' ||
    firstChar === '＠';

  if (isDangerous) {
    return `'${value}`;
  }

  return value;
};

/**
 * Format a field for RFC 4180 compliant CSV output.
 * Quotes fields that contain commas, double quotes, or newlines.
 * Escapes internal double quotes by doubling them.
 *
 * Fields that contain a semicolon or a tab are quoted too, which RFC 4180
 * allows for any field. This does NOT stop a spreadsheet that splits on
 * semicolon or tab from starting a new cell in the middle of a field: there a
 * quote only counts at the start of a field, so `a,"x;=1+1"` still splits
 * into `a,"x` and `=1+1"`.
 *
 * @param field - The field value to format
 * @returns Properly formatted CSV field
 */
export const formatCsvField = (field: string): string => {
  if (
    field.includes(',') ||
    field.includes('"') ||
    field.includes('\n') ||
    field.includes('\r') ||
    field.includes(';') ||
    field.includes('\t')
  ) {
    // Escape quotes by doubling them, then wrap in quotes
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
};

/**
 * One CSV cell: the formula guard, then RFC 4180 quoting. Every cell of every
 * export, header included, is written with this (#2194). An absent value is
 * an empty cell. `String`, because stored data typed as a string can hold
 * another value at run time.
 *
 * @param value - Raw cell value
 * @returns The cell as it is written to the file
 */
export const csvCell = (value: string | null | undefined): string => {
  if (value === undefined || value === null) return '';
  return formatCsvField(sanitizeCsvField(String(value)));
};
